import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, LogOut, Mail, Menu, Settings, Settings2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '../../ui/button.tsx';
import { cn } from '../../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown.tsx';
import StaffNotificationsDropdown from './StaffNotificationsDropdown.jsx';
import useAuth from '../../../hooks/useAuth.js';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar.tsx';
import { typography, colors, components } from '../../../utils/styles';
import { LoadingSpinner } from '../../ui/loading-spinner.tsx';

// Enhanced getInitials function for better display
const getInitials = (firstName, lastName, email) => {
  // If we have first and last name, use the first letter of each
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }

  // If we only have first name
  if (firstName && !lastName) {
    return firstName.substring(0, 2).toUpperCase();
  }

  // Fall back to email if no name is available
  if (email) {
    return email.substring(0, 2).toUpperCase();
  }

  // Last resort
  return 'U';
};

export default function Header({
  searchQuery,
  setSearchQuery,
  title,
  subtitle,
  isMobile,
  toggleSidebar,
}) {
  const { user: staffProfile, logout, isLoggingOut } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  // Calculate user display details using memoization
  const userDetails = useMemo(() => {
    if (!staffProfile)
      return {
        displayName: 'User',
        role: 'Guest',
        initials: 'U',
        photoUrl: '/placeholder.svg',
        email: '',
        isVerified: false,
      };

    const userData = staffProfile;
    const displayName =
      userData.name ||
      `${userData.firstName || ''} ${userData.lastName || ''}`.trim() ||
      userData.email;
    const role = userData.roleName || userData.role || 'User';
    const initials = getInitials(
      userData.firstName,
      userData.lastName,
      userData.email
    );
    const photoUrl = userData.photoUrl || userData.avatar || '/placeholder.svg';
    const email = userData.email || '';
    const isVerified = userData.verified || false;

    return { displayName, role, initials, photoUrl, email, isVerified };
  }, [staffProfile]);

  const handleLogout = () => {
    logout(undefined, {
      onSuccess: () => {
        navigate('/login', { replace: true });
      },
      onError: (error) => {
        toast.error(error.message || 'Logout Failed! Please try again');
      },
    });
  };

  const handleEmailVerification = () => {
    navigate('/otp', {
      state: {
        email: userDetails.email,
        fromDashboard: true,
      },
    });
  };

  return (
    <header
      className={`${components.card} border-b border-gray-200 h-16 sm:h-20`}
    >
      <div className="px-4 sm:px-8 h-full flex items-center justify-between">
        {/* Left section with hamburger menu on mobile */}
        <div className="flex items-center gap-3">
          {/* Mobile menu toggle button */}
          {isMobile && (
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 sm:hidden"
              onClick={toggleSidebar}
              aria-label="Toggle menu"
            >
              <Menu className="h-5 w-5 text-gray-700" />
            </Button>
          )}

          <div>
            <h1
              className={`${typography.h1} ${colors.text.primary} text-lg sm:text-xl`}
            >
              {title}
            </h1>
            <p className="text-xs sm:text-sm text-gray-500">{subtitle}</p>
          </div>
        </div>

        {/* Right section with actions */}
        <div className="flex items-center space-x-2 sm:space-x-6">
          {/* Staff Notifications */}
          <div className={isMobile ? 'scale-90' : ''}>
            <StaffNotificationsDropdown />
          </div>

          {/* User Profile Section - Now using dropdown menu for both verified and unverified users */}
          <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger
              className="flex items-center space-x-3 focus:outline-none disabled:opacity-50"
              disabled={isLoggingOut}
            >
              <Avatar
                className={`h-8 w-8 sm:h-10 sm:w-10 ${
                  !userDetails.isVerified ? 'ring-2 ring-red-500' : ''
                }`}
              >
                <AvatarImage src={userDetails.photoUrl} />
                <AvatarFallback
                  className={`${
                    userDetails.isVerified
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-red-50 text-red-700'
                  } font-semibold text-xs sm:text-sm`}
                >
                  {userDetails.initials}
                </AvatarFallback>
              </Avatar>
            </DropdownMenuTrigger>

            <DropdownMenuContent className="w-64 mt-1 p-1" align="end">
              <div className="flex items-center gap-3 px-3 py-2">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={userDetails.photoUrl} />
                  <AvatarFallback
                    className={`${
                      userDetails.isVerified
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-red-50 text-red-700'
                    } font-semibold`}
                  >
                    {userDetails.initials}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col">
                  <div className="flex items-center gap-1">
                    <p className="text-sm font-medium text-gray-900">
                      {userDetails.displayName}
                    </p>
                    {userDetails.isVerified && (
                      <Check className="w-4 h-4 text-green-500" />
                    )}
                  </div>
                  <p className={`${typography.small} ${colors.text.muted}`}>
                    {userDetails.role}
                  </p>
                </div>
              </div>

              <DropdownMenuSeparator />

              {/* Verification option for unverified users */}
              {!userDetails.isVerified && (
                <>
                  <DropdownMenuItem
                    className="flex items-center px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50"
                    onSelect={(e) => {
                      e.preventDefault();
                      handleEmailVerification();
                    }}
                  >
                    <Mail className="w-4 h-4 mr-2" /> Verify Email
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}

              <DropdownMenuItem
                className="flex items-center px-3 py-2 text-sm cursor-pointer"
                onSelect={(e) => {
                  e.preventDefault();
                  navigate('/settings', {
                    state: { label: 'Account Information' },
                  });
                }}
              >
                <Settings className="w-4 h-4 mr-2" /> Profile Settings
              </DropdownMenuItem>

              {/* Only admins should see this - start */}
              {staffProfile.Role.baseRole === 'superadmin' ? (
                <DropdownMenuItem
                  className="flex items-center px-3 py-2 text-sm cursor-pointer"
                  onSelect={(e) => {
                    e.preventDefault();
                    navigate('/org/settings', {
                      state: { label: 'General Settings' },
                    });
                  }}
                >
                  <Settings2 className="w-4 h-4 mr-2" /> School Settings
                </DropdownMenuItem>
              ) : null}
              {/* Only admins should see this - end */}

              <DropdownMenuItem
                className={`flex items-center px-3 py-2 text-sm text-red-600 
                  ${
                    !isLoggingOut
                      ? 'hover:text-red-700 hover:bg-red-50'
                      : 'opacity-50 cursor-not-allowed'
                  }`}
                onSelect={(e) => {
                  e.preventDefault();
                  if (!isLoggingOut) handleLogout();
                }}
                disabled={isLoggingOut}
              >
                <div className="flex items-center w-full">
                  {isLoggingOut ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      <span>Logging out...</span>
                    </>
                  ) : (
                    <>
                      <LogOut className="w-4 h-4 mr-2" />
                      <span>Logout</span>
                    </>
                  )}
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
